import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Breadcrumbs,
  Link,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  CheckHexagon,
  Activity,
  Tool,
  Hash,
  Component,
  DangerHexagon,
  DangerTriangle,
  FilterOne,
  SearchWaves,
  Globe,
  Calendar,
  Wrench,
  ArrowLeft,
  List,
} from "@mynaui/icons-react";
import Footer from "../../../shared/components/Footer";
import ConnectedSensors from "../components/ConnectedSensors";
import LogsAndEvents from "../components/LogsAndEvents";
import RiskAlertHistory from "../components/RiskAlertHistory";
import FailureHistory from "../components/FailureHistory";
import ServiceHistory from "../components/ServiceHistory";
import WorkOrdersSection from "../components/WorkOrdersSection";
import CommentsSection from "../components/CommentsSection";
import HealthCheckModal from "../../dashboard/components/HealthCheckModal";
import PartsAvailabilityModal from "../../dashboard/components/PartsAvailabilityModal";
import ServiceProgressModal from "../../dashboard/components/ServiceProgressModal";
import { useAuth } from "../../../shared/context/AuthContext";
import { ROLES } from "../../../shared/config/roleConfig";
import assetService from "../../../services/assetService";

const detailFields = [
  { key: "id", label: "ID", icon: Hash },
  { key: "riskLevel", label: "Status", icon: DangerHexagon },
  { key: "model", label: "Model", icon: Component },
  { key: "type", label: "Type", icon: FilterOne },
  { key: "make", label: "Make", icon: SearchWaves },
  { key: "detailLocation", label: "Location", icon: Globe },
];

const EmptyState = ({ message }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      py: 4,
      border: "1px dashed #E2E8F0",
      borderRadius: "8px",
      mb: 3,
    }}
  >
    <Typography sx={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
      {message}
    </Typography>
  </Box>
);

const scrollbarStyles = {
  "&::-webkit-scrollbar": { width: "3px" },
  "&::-webkit-scrollbar-thumb": {
    background: "#CBD5E1",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  scrollbarWidth: "thin",
  scrollbarColor: "#CBD5E1 transparent",
};


const getStatusLabel = (riskLevel) => {
  const level = riskLevel?.toLowerCase();

  if (level === "critical") return "Critical";
  if (level === "high") return "High Risk";
  if (level === "medium") return "Medium Risk";
  if (level === "low") return "Low Risk";

  return "Unknown";
};

const getStatusColor = (riskLevel) => {
  const level = riskLevel?.toLowerCase();

  if (level === "high" || level === "critical") return "#DC2626"; // red
  if (level === "medium") return "#D97706"; // yellow
  if (level === "low") return "#16A34A"; // green

  return "#020618";
};

const getHealthScoreColor = (score) => {
  if (score >= 90) return "#22C55E";
  if (score >= 75) return "#f9a320";
  return "#DC2626";                 
};


const AssetDetailPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTechnician = user?.role === ROLES.TECHNICIAN;

  const [asset, setAsset] = useState(null);
  const [sensors, setSensors] = useState(null);
  const [flowInsights, setFlowInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [failureHistory, setFailureHistory] = useState([]);
  const [serviceHistory, setServiceHistory] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [healthCheckOpen, setHealthCheckOpen] = useState(false);
  const [partsCheckOpen, setPartsCheckOpen] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [partsAvailability, setPartsAvailability] = useState(null);
  const [serviceProgressOpen, setServiceProgressOpen] = useState(false);
  const [serviceComplete, setServiceComplete] = useState(false);
  const [comments, setComments] = useState([]);
  const [recommendedAction, setRecommendedAction] = useState(null);

  useEffect(() => {
    assetService
      .getAssetSummary(assetId)
      .then((res) => {
        const data = res.data.data;
        setAsset({
          ...data,
          detailLocation: data.location?.full ?? "",
        });
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));

    assetService
      .getSensorReadings(assetId)
      .then((res) => setSensors(res.data.data))
      .catch(() => {});

    assetService
      .getAutomatedFlowInsights(assetId)
      .then((res) => {
        setFlowInsights(res.data.data);
        setRecommendedAction(res.data.data.recommendedAction);
      })
      .catch(() => {});

    assetService
      .getAlertHistory(assetId)
      .then((res) => setAlertHistory(res.data.data.alerts ?? []))
      .catch(() => {});

    assetService
      .getFailureHistory(assetId)
      .then((res) => setFailureHistory(res.data.data.failures ?? []))
      .catch(() => {});

    assetService
      .getServiceHistory(assetId)
      .then((res) => setServiceHistory(res.data.data.serviceHistory ?? []))
      .catch(() => {});

    assetService
      .getWorkOrders(assetId)
      .then((res) => setWorkOrders(res.data.data.workOrders ?? []))
      .catch(() => {});

    assetService
      .getComments(assetId)
      .then((res) => {
        setComments(res.data.data.comments ?? []);
      })
      .catch(() => {});
  }, [assetId]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !asset) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="h5">Asset not found</Typography>
        <Button onClick={() => navigate("/assets")} sx={{ mt: 2 }}>
          Back to Assets
        </Button>
      </Box>
    );
  }

  const handleStartService = () => {
    setServiceProgressOpen(true);
    setServiceComplete(false);
  };

  const handleServiceComplete = () => {
    setServiceComplete(true);
  };

  const handleServiceModalClose = () => {
    setServiceProgressOpen(false);
  };

  const handleHealthCheckComplete = (data) => {
    setHealthData(data);
    setPartsAvailability(data.partsAvailable);
  };

  const handlePartsCheck = () => {
    setPartsCheckOpen(true);
  };

  const handlePartsCheckComplete = (partsAvailable) => {
    setPartsAvailability(partsAvailable);
    setPartsCheckOpen(false);
  };

  const statusColor = getStatusColor(asset.riskLevel);
  const statusLabel = getStatusLabel(asset.riskLevel);
  const isEmergency =
    asset.recommendedAction?.severity?.toLowerCase() ===
    "emergency maintenance";

  return (
    <Box
      sx={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFF",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 4 }, py: 3, flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          {/* Asset name + technician buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{
                pb: 1,
                fontWeight: 500,
                fontSize: "20px",
                lineHeight: "28px",
                color: "#020618",
              }}
            >
              {asset.fullName}
            </Typography>

            {isTechnician && (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                {partsAvailability !== null && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: "pointer",
                    }}
                    onClick={handlePartsCheck}
                  >
                    <CheckHexagon
                      style={{
                        fontSize: 20,
                        color: partsAvailability ? "#16A34A" : "#DC2626",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: partsAvailability ? "#16A34A" : "#DC2626",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      }}
                    >
                      {partsAvailability
                        ? "All Parts Available"
                        : "Parts Not Available"}
                    </Typography>
                  </Box>
                )}
                <Button
                  variant="outlined"
                  startIcon={<Activity />}
                  onClick={() => setHealthCheckOpen(true)}
                  sx={{
                    borderColor: "#E5E7EB",
                    color: "text.primary",
                    fontWeight: 600,
                    px: 2.5,
                    textTransform: "none",
                    "&:hover": { borderColor: "#D1D5DB", bgcolor: "#F9FAFB" },
                  }}
                >
                  Run Health Check
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Tool />}
                  onClick={handleStartService}
                  //disabled={partsAvailability !== true}
                  sx={{
                    bgcolor: "#22C55E",
                    color: "#fff",
                    fontWeight: 600,
                    px: 2.5,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#16A34A" },
                  }}
                >
                  Start Service
                </Button>
              </Box>
            )}
          </Box>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            sx={{ mb: 1 }}
          >
            <Link
              component="button"
              underline="none"
              onClick={() => {
                !isTechnician ? navigate("/assets") : navigate("/dashboard");
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#62748E",
                fontSize: "14px",
                lineHeight: "20px",
                fontWeight: 400,
              }}
            >
              <ArrowLeft size={16} color="#62748E" strokeWidth={1.5} />
              Assets
            </Link>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "20px",
                color: "#020618",
                fontWeight: 600,
              }}
            >
              {asset.fullName}
            </Typography>
          </Breadcrumbs>
        </Box>

        {/* Asset Info Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
            mb: 3,
          }}
        >
          {/* Image */}
          <Card
            elevation={0}
            sx={{
              width: { xs: "100%", md: "500.69px" },
              height: { xs: "240px", sm: "300px", md: "352px" },
              flexShrink: 0,
              overflow: "hidden",
              borderRadius: 0.5,
              boxShadow: "none",
            }}
          >
            <Box
              component="img"
              src={asset.image}
              alt={asset.fullName}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                borderRadius: "12px",
              }}
            />
          </Card>

          {/* Detail Fields */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            {/* Row 1: ID + Status */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "20px",
              }}
            >
              {detailFields.slice(0, 2).map((field) => {
                const Icon = field.icon;
                let value = asset[field.key];
                let valueColor = "#020618";
                if (field.key === "riskLevel") {
                  value = statusLabel;
                  valueColor = statusColor;
                }
                return (
                  <Box
                    key={field.key}
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "16px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                  >
                    <Icon size={22} color="#020618" strokeWidth={1.5} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#62748E",
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: valueColor,
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* Row 2: Model + Type */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "20px",
              }}
            >
              {detailFields.slice(2, 4).map((field) => {
                const Icon = field.icon;
                const value = asset[field.key];
                return (
                  <Box
                    key={field.key}
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      padding: "16px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                    }}
                  >
                    <Icon size={22} color="#020618" strokeWidth={1.5} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#62748E",
                        }}
                      >
                        {field.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: "14px",
                          lineHeight: "20px",
                          color: "#020618",
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* Row 3 & 4: Make, Location (full width) */}
            {detailFields.slice(4).map((field) => {
              const Icon = field.icon;
              const value = asset[field.key];
              return (
                <Box
                  key={field.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    padding: "16px",
                    border: "1px solid #E2E8F0",
                    borderRadius: "8px",
                  }}
                >
                  <Icon size={22} color="#020618" strokeWidth={1.5} />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#62748E",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: "#020618",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

{/* Recommended Action — full width */}
<Card
          elevation={0}
          sx={{
            borderRadius: 1,
            bgcolor: isEmergency ? "#FFC9C9" : "#FEE685",
            border: `1px solid ${isEmergency ? "#C10007" : "#BB4D00"}`,
            boxShadow: "none",
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <Wrench size={22} color="#020618" strokeWidth={1.5} />
              <Typography
                sx={{ fontWeight: 500, fontSize: "16px", lineHeight: "24px", color: "#020618" }}
              >
                Recommended Action
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: isEmergency ? "#DC2626" : "#92400E",
                fontSize: "0.8125rem",
              }}
            >
              {recommendedAction}
            </Typography>
          </CardContent>
        </Card>

        {/* Bottom Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            },
            gap: 2.5,
            mb: 2.5,
          }}
        >
          {/* Health Score */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              background: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              boxShadow: "none",
            }}
          >
            <CardContent
              sx={{ padding: "24px", "&:last-child": { pb: "24px" } }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#020618",
                  }}
                >
                  Health Score
                </Typography>
                <Activity size={30} color="#020618" strokeWidth={1.5} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  lineHeight: "32px",
                  color: getHealthScoreColor(asset.healthScore),
                  mb: 1,
                }}
              >
                {asset.healthScore}/100
              </Typography>
              <LinearProgress
                variant="determinate"
                value={asset.healthScore}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "#E5E7EB",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    bgcolor: getHealthScoreColor(asset.healthScore),
                  },
                }}
              />
            </CardContent>
          </Card>

          {/* RUL */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 1,
              border: "1px solid #E0E0E0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#020618",
                  }}
                >
                  RUL
                </Typography>
                <Calendar size={30} color="#020618" strokeWidth={1.5} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  lineHeight: "32px",
                  color: "#0A0A0A",
                  mb: 1,
                }}
              >
                {asset.rul?.value || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#555555",
                }}
              >
                Remaining Useful Life
              </Typography>
            </CardContent>
          </Card>

          {/* Failure Probability */}
          <Card
            elevation={0}
            sx={{
              flex: 1,
              borderRadius: 1,
              border: "1px solid #E0E0E0",
              boxShadow: "none",
            }}
          >
            <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "16px",
                    lineHeight: "24px",
                    color: "#020618",
                  }}
                >
                  Failure Probability
                </Typography>
                <DangerTriangle size={30} color="#020618" strokeWidth={1.5} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "24px",
                  lineHeight: "32px",
                  color: "#0A0A0A",
                  mb: 1,
                }}
              >
                {asset.failureProbability}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 400,
                  fontSize: "14px",
                  lineHeight: "20px",
                  color: "#555555",
                }}
              >
                {asset.failureTimeframe}
              </Typography>
            </CardContent>
          </Card>

        </Box>

        

        {/* Connected Sensors*/}

        <ConnectedSensors sensors={sensors} />

        {/* Logs and Events */}

        <LogsAndEvents flowInsights={flowInsights} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2.5,
            mb: 3,
          }}
        >
          {/* Risk Alert History */}
          <Box
            sx={{
              minWidth: 0,
              maxHeight: 400,
              overflowY: "auto",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              ...scrollbarStyles,
            }}
          >
            {alertHistory.length > 0 ? (
              <RiskAlertHistory alerts={alertHistory} />
            ) : (
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <List size={24} color="#020618" strokeWidth={1.5} />
                  <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "28px", color: "#111827" }}>
                    Risk Alert History
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
                    No risk alert history found.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Failure History */}
          <Box
            sx={{
              minWidth: 0,
              maxHeight: 400,
              overflowY: "auto",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              ...scrollbarStyles,
            }}
          >
            {failureHistory.length > 0 ? (
              <FailureHistory failures={failureHistory} />
            ) : (
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <List size={24} color="#020618" strokeWidth={1.5} />
                  <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "28px", color: "#111827" }}>
                    Failure History
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
                    No failure history found.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2.5,
            mb: 3,
          }}
        >
          {/* Service History */}
          <Box
            sx={{
              minWidth: 0,
              maxHeight: 400,
              overflowY: "auto",
              borderRadius: "12px",
              border: "1px solid #CAD5E2",
              background: "#F1F5F9",
              ...scrollbarStyles,
            }}
          >
            {serviceHistory.length > 0 ? (
              <ServiceHistory services={serviceHistory} />
            ) : (
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Wrench size={24} color="#020618" strokeWidth={1.5} />
                  <Typography sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "28px", color: "#111827" }}>
                    Service History
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 80 }}>
                  <Typography sx={{ fontSize: "0.875rem", color: "#9CA3AF" }}>
                    No service history found.
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Work Orders */}
        {workOrders.length > 0 ? (
          <WorkOrdersSection workOrders={workOrders} />
        ) : (
          <EmptyState message="No work orders found for this asset." />
        )}

        {/* Comments */}
        <CommentsSection
          assetId={assetId}
          comments={comments}
          setComments={setComments}
          currentUser={user}
        />
      </Box>
      <Footer />

      {/* Technician Modals */}
      {isTechnician && (
        <>
          <HealthCheckModal
            open={healthCheckOpen}
            onClose={() => setHealthCheckOpen(false)}
            onHealthCheckComplete={handleHealthCheckComplete}
            assetId={assetId}
            assetName={asset.fullName}
          />
          <PartsAvailabilityModal
            open={partsCheckOpen}
            onClose={() => setPartsCheckOpen(false)}
            onComplete={handlePartsCheckComplete}
            healthData={healthData}
          />
          <ServiceProgressModal
            open={serviceProgressOpen}
            onClose={handleServiceModalClose}
            onComplete={handleServiceComplete}
            isComplete={serviceComplete}
          />
        </>
      )}
    </Box>
  );
};

export default AssetDetailPage;
